// Known exception to the utils layer rule: this module calls the API (see src/utils/CLAUDE.md).
// eslint-disable-next-line no-restricted-imports
import { runwayApi } from "@api/index";
import { USER_URL } from "@utils/constantsEnv";

export const MARKETING_EMAIL_LOOKUP_TIMEOUT_MS = 3000;

type GetFn = (path: string, config: { timeout: number }) => Promise<{ data: { email?: unknown } }>;

const defaultGet: GetFn = (path, config) => runwayApi.get(`${USER_URL}${path}`, config);

// Every failure (404, network error, timeout) resolves to null instead of throwing.
export async function fetchMarketingEmail(
  visitorId: string,
  timeoutMs: number = MARKETING_EMAIL_LOOKUP_TIMEOUT_MS,
  get: GetFn = defaultGet
): Promise<string | null> {
  try {
    const response = await get(`marketing-email/${encodeURIComponent(visitorId)}`, {
      timeout: timeoutMs,
    });
    const email = response?.data?.email;
    return typeof email === "string" && email ? email : null;
  } catch {
    return null;
  }
}

let activeVisitorId: string | undefined;
let activeLookup: Promise<void> | undefined;

/** Test-only: clears the module-level dedup/stale-response guard state. */
export function resetEmailHandoffState(): void {
  activeVisitorId = undefined;
  activeLookup = undefined;
}

export interface RunEmailHandoffParams {
  visitorId: string | undefined;
  getCurrentEmail: () => string;
  applyEmail: (email: string) => void;
  fetchEmail?: (visitorId: string) => Promise<string | null>;
}

export async function runEmailHandoff(params: RunEmailHandoffParams): Promise<void> {
  const { visitorId, getCurrentEmail, applyEmail, fetchEmail = fetchMarketingEmail } = params;

  if (!visitorId) return;

  if (visitorId === activeVisitorId && activeLookup) {
    return activeLookup;
  }

  activeVisitorId = visitorId;

  const lookup = (async () => {
    const email = await fetchEmail(visitorId);

    if (visitorId !== activeVisitorId) return; // superseded by a newer visitor id
    if (!email) return;
    if (getCurrentEmail()) return; // never overwrite an email already present

    applyEmail(email);
  })();

  activeLookup = lookup;
  return lookup;
}
